import '@livekit/components-styles';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LiveKitRoom } from '@livekit/components-react';
import type { DisconnectReason } from 'livekit-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { TeleattendanceVideoConnection } from '../services/teleattendanceService';
import IonSFUVideoConference from './video-conference/IonSFUVideoConference';
import LiveKitMeetingSounds from './video-conference/LiveKitMeetingSounds';
import LiveKitVideoLayout from './video-conference/LiveKitVideoLayout';
import MicrophoneGainProcessor from './video-conference/MicrophoneGainProcessor';
import { playMeetingSound } from './video-conference/meetingSounds';
import type {
  ParticipantModerationAction,
  MeetingChatConfig,
  VideoLabels,
  VideoLayoutVariant,
} from './video-conference/types';

export type { ParticipantModerationAction } from './video-conference/types';

interface TeleattendanceVideoConferenceProps {
  connection: TeleattendanceVideoConnection;
  title?: string;
  description?: string;
  videoLabels?: VideoLabels;
  hideParticipantMetadata?: boolean;
  supplementalContent?: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layoutVariant: VideoLayoutVariant;
  presentation?: 'dialog' | 'page';
  pageBrand?: ReactNode;
  pageActions?: ReactNode;
  pageHeaderHidden?: boolean;
  internalControls?: ReactNode;
  initialAudioEnabled?: boolean;
  initialVideoEnabled?: boolean;
  initialMicrophoneSensitivity?: number;
  initialDeviceIds?: {
    audioInputDeviceId?: string;
    videoInputDeviceId?: string;
    audioOutputDeviceId?: string;
  };
  onModerateParticipant?: (
    participantIdentity: string,
    action: ParticipantModerationAction,
    reason?: string,
  ) => void;
  moderationPending?: boolean;
  recordingMode?: boolean;
  chatConfig?: MeetingChatConfig;
  onDisconnected?: (reason?: DisconnectReason) => void;
}

const videoLabelByLayout: Record<VideoLayoutVariant, VideoLabels> = {
  doctor: {
    main: 'Paciente',
    preview: 'Sua câmera',
  },
  patient: {
    main: 'Médico',
    preview: 'Sua câmera',
  },
};

const modalDescriptionByLayout: Record<VideoLayoutVariant, string> = {
  doctor:
    'Atendimento em andamento. O paciente fica em destaque e sua câmera permanece como miniatura.',
  patient:
    'Consulta em andamento. O médico fica em destaque e sua câmera permanece como miniatura.',
};

const TeleattendanceVideoConference = ({
  connection,
  title,
  description,
  videoLabels,
  hideParticipantMetadata = false,
  supplementalContent,
  open,
  onOpenChange,
  layoutVariant,
  presentation = 'dialog',
  pageBrand,
  pageActions,
  pageHeaderHidden = false,
  internalControls,
  initialAudioEnabled = true,
  initialVideoEnabled = true,
  initialMicrophoneSensitivity = 100,
  initialDeviceIds,
  onModerateParticipant,
  moderationPending = false,
  recordingMode = false,
  chatConfig,
  onDisconnected,
}: TeleattendanceVideoConferenceProps) => {
  const { t } = useTranslation();
  const microphoneGainProcessor = useMemo(
    () => new MicrophoneGainProcessor(initialMicrophoneSensitivity / 100),
    [initialMicrophoneSensitivity],
  );
  if (!connection.serverUrl) {
    return null;
  }

  const isLiveKitProvider = connection.provider === 'livekit';
  const isIonProvider = connection.provider === 'ion-sfu';

  if (isLiveKitProvider && !connection.token) {
    return null;
  }

  if (!isLiveKitProvider && !isIonProvider) {
    return null;
  }

  const resolvedTitle =
    title || t('teleattendance.video_modal_title') || 'Videoconferência';
  const resolvedDescription =
    description !== undefined
      ? description
      : t(
          layoutVariant === 'doctor'
            ? 'teleattendance.video_modal_description_doctor'
            : 'teleattendance.video_modal_description_patient',
        ) || modalDescriptionByLayout[layoutVariant];
  const resolvedVideoLabels = videoLabels || videoLabelByLayout[layoutVariant];
  const initialAudioCapture = recordingMode
    ? false
    : initialAudioEnabled
      ? {
          autoGainControl: false,
          ...(initialDeviceIds?.audioInputDeviceId
            ? { deviceId: initialDeviceIds.audioInputDeviceId }
            : {}),
        }
      : false;
  const initialVideoCapture = recordingMode
    ? false
    : initialVideoEnabled
      ? initialDeviceIds?.videoInputDeviceId
        ? { deviceId: initialDeviceIds.videoInputDeviceId }
        : true
      : false;

  const conferenceRoom = (
    <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
      {supplementalContent ? (
        <div
          className={`min-h-0 border border-border/70 bg-background/70 lg:h-full lg:w-[20%] ${
            presentation === 'page' ? 'rounded-lg' : 'rounded-[28px]'
          }`}
        >
          <div className="h-full max-h-[24vh] overflow-y-auto px-4 py-4 lg:max-h-none">
            {supplementalContent}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 min-w-0 flex-1 lg:w-[80%]">
        {isLiveKitProvider ? (
          <LiveKitRoom
            token={connection.token}
            serverUrl={connection.serverUrl}
            connect
            video={initialVideoCapture}
            audio={initialAudioCapture}
            onConnected={() => {
              if (!recordingMode) playMeetingSound('join');
            }}
            onDisconnected={(reason) => {
              if (!recordingMode) playMeetingSound('leave');
              onDisconnected?.(reason);
            }}
            className={
              presentation === 'page'
                ? 'h-full focus:outline-none'
                : 'focus:outline-none'
            }
          >
            <LiveKitMeetingSounds />
            <LiveKitVideoLayout
              videoLabels={resolvedVideoLabels}
              hideParticipantMetadata={hideParticipantMetadata}
              fillAvailableHeight={presentation === 'page'}
              onClose={
                presentation === 'page' ? () => onOpenChange(false) : undefined
              }
              onModerateParticipant={onModerateParticipant}
              moderationPending={moderationPending}
              recordingMode={recordingMode}
              initialDeviceIds={initialDeviceIds}
              initialVideoEnabled={initialVideoEnabled}
              initialMicrophoneSensitivity={initialMicrophoneSensitivity}
              microphoneGainProcessor={microphoneGainProcessor}
              internalControls={internalControls}
              chatConfig={chatConfig}
            />
          </LiveKitRoom>
        ) : (
          <IonSFUVideoConference
            connection={connection}
            videoLabels={resolvedVideoLabels}
          />
        )}
      </div>
    </div>
  );

  if (presentation === 'page') {
    return (
      <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-muted/30">
        {!pageHeaderHidden ? (
          <header className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border/70 bg-background px-3 py-2 sm:flex-nowrap sm:px-5">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              {pageBrand ? (
                <div className="flex shrink-0 items-center">{pageBrand}</div>
              ) : null}
              <div
                className={
                  pageBrand ? 'min-w-0 border-l border-border pl-4' : 'min-w-0'
                }
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 ring-4 ring-emerald-500/10"
                    aria-hidden="true"
                  />
                  <h1 className="truncate text-base font-semibold text-card-foreground">
                    {resolvedTitle}
                  </h1>
                </div>
                {resolvedDescription ? (
                  <p className="truncate text-xs text-muted-foreground sm:text-sm">
                    {resolvedDescription}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {pageActions}
            </div>
          </header>
        ) : null}

        <main
          className={`min-h-0 flex-1 overflow-hidden ${
            pageHeaderHidden ? 'p-0' : 'p-1.5 sm:p-3'
          }`}
        >
          {conferenceRoom}
        </main>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[96vh] max-w-[96vw] gap-0 overflow-hidden border-none bg-transparent p-0 shadow-none sm:max-w-[92vw]">
        <div className="flex max-h-[96vh] w-full flex-col overflow-hidden rounded-[30px] border border-[#bfdbfe] bg-card shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
          <DialogHeader className="border-b border-border/70 bg-background/95 px-5 py-4 text-left sm:px-6">
            <DialogTitle className="text-card-foreground">
              {resolvedTitle}
            </DialogTitle>
            {resolvedDescription ? (
              <DialogDescription className="text-muted-foreground">
                {resolvedDescription}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <div className="flex-1 overflow-hidden bg-card p-3 sm:p-4">
            {conferenceRoom}
          </div>

          <div className="flex shrink-0 justify-end border-t border-border/70 bg-background/90 px-5 py-4 sm:px-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('teleattendance.close_call') || 'Fechar chamada'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeleattendanceVideoConference;
